import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useTu } from "@src/appV2/i18n/useTu";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";

import { useGetRosterWindowSettings } from "../api/useGetRosterWindowSettings";
import { useUpdateRosterWindowSettings } from "../api/useUpdateRosterWindowSettings";
import { type RosterWindowRules } from "@clipboard-health/contract-backend-main";
import {
  RosterWindowEditorErrorView,
  RosterWindowEditorLoadingView,
  RosterWindowEditorView,
} from "./RosterWindowEditorView";

interface RosterWindowEditorProps {
  workplaceId: string;
}

export function RosterWindowEditor(props: RosterWindowEditorProps) {
  const { workplaceId } = props;
  const { tu } = useTu("settings");
  const { showSuccessToast, showErrorToast } = useToast();
  const isClipboardRosterEnabled = useCbhFlag(CbhFeatureFlag.CLIPBOARD_ROSTER_ENABLED, {
    defaultValue: false,
  });
  const { enabled: isRosterWindowRedesignEnabled } = useCbhFlag(
    CbhFeatureFlag.ROSTER_WINDOW_REDESIGN_ENABLED,
    { defaultValue: { enabled: false } }
  );
  const {
    data: settings,
    isLoading,
    isError,
    refetch,
  } = useGetRosterWindowSettings({
    workplaceId,
    enabled: isClipboardRosterEnabled && isRosterWindowRedesignEnabled,
  });
  const updateSettings = useUpdateRosterWindowSettings();

  if (!isClipboardRosterEnabled || !isRosterWindowRedesignEnabled) {
    return null;
  }

  if (isLoading) {
    return <RosterWindowEditorLoadingView />;
  }

  if (isError || !settings) {
    return <RosterWindowEditorErrorView onRetry={() => void refetch()} />;
  }

  const handleFieldChange = (key: keyof RosterWindowRules, value: number) => {
    const nextAttributes: RosterWindowRules = {
      longLeadThresholdDays: settings.longLeadThresholdDays,
      longLeadOpenDaysBeforeStart: settings.longLeadOpenDaysBeforeStart,
      shortLeadOpenDaysAfterPosting: settings.shortLeadOpenDaysAfterPosting,
      urgentThresholdDays: settings.urgentThresholdDays,
      [key]: value,
    };
    updateSettings.mutate(
      { workplaceId, attributes: nextAttributes },
      {
        onSuccess: () => {
          showSuccessToast(tu("Roster settings saved."));
        },
        onError: () => {
          showErrorToast(tu("Failed to save roster settings. Please try again."));
        },
      }
    );
  };

  const handleToggleShiftCancellation = (checked: boolean) => {
    updateSettings.mutate(
      { workplaceId, attributes: { allowShiftCancellation: checked } },
      {
        onSuccess: () => {
          showSuccessToast(tu("Shift cancellation setting updated."));
        },
        onError: () => {
          showErrorToast(tu("Failed to update shift cancellation setting. Please try again."));
        },
      }
    );
  };

  return (
    <RosterWindowEditorView
      settings={{
        longLeadThresholdDays: settings.longLeadThresholdDays,
        longLeadOpenDaysBeforeStart: settings.longLeadOpenDaysBeforeStart,
        shortLeadOpenDaysAfterPosting: settings.shortLeadOpenDaysAfterPosting,
        urgentThresholdDays: settings.urgentThresholdDays,
      }}
      allowShiftCancellation={settings.allowShiftCancellation}
      isSaving={updateSettings.isLoading}
      onChange={handleFieldChange}
      onToggleShiftCancellation={handleToggleShiftCancellation}
    />
  );
}
